import React from 'react';
import { Body7, Container } from '@ui';
import { TextField } from '@ui/inputs';
import { SpecEditorContext } from './SpecEditorContext';
import { runVariablesFor } from './specSchema';
import { unknownVariables } from './jinjaVars';
import { Chip, Consequence, MicroLabel, Row } from './styles';

// `prompt` and `format_prompt` are the two longest and most-iterated fields in the schema, and
// until now they were string cells in a generic JSON tree. Here they get height.
//
// The variable rail is per DOCUMENTS KIND, because the engine's namespace is
// (factor_runner.py:259-310 — see RUN_JINJA_VARIABLES). It used to publish preview_score's fixed
// seven for every kind, which was wrong for three of the four and wrong in both directions: it
// offered doc_id / doc_type / published_at / available_to_query_at to filter, screen and
// related_filter specs, where none of them exist, and flagged doc_types / as_of / date_from /
// date_to / relationship_kinds as unknown where they are the correct names.
//
// An unknown {{token}} is ADVICE, not an error, and that stays deliberate: the loader only COMPILES
// the templates, so a spec naming a variable that cannot exist locks cleanly. It dies afterwards,
// at run time, permanently — which is what the warning now says instead of the old (wrong) claim
// that Jinja renders it as empty.

export const PromptEditor = ({ path, label, hint, tall = false, showVariables = false }) => {
  const { definition, setValue } = React.useContext(SpecEditorContext);

  const value = ((definition || {}).score_gen || {})[path] || '';
  const documentsKind = (((definition || {}).document_set || {}).documents || {}).kind;
  const variables = runVariablesFor(documentsKind);
  const unknown = unknownVariables(value, variables);
  // preview_score always substitutes the document-shaped context, so it is a faithful rehearsal for
  // exactly one kind. UNVERIFIED since fapi #492 — see the note on PREVIEW_JINJA_VARIABLES in
  // specSchema.js. The warning below is deliberately left standing until fapi confirms, because
  // replacing one false claim with another is not an improvement.
  const previewLies = !!variables && documentsKind !== 'triggering_document';
  const fieldId = `prompt-editor-${path}`;

  return (
    <div>
      <TextField
        id={fieldId}
        multiline
        mono
        rows={tall ? 16 : 9}
        label={label}
        value={value}
        onChange={(next) => setValue(`score_gen.${path}`, next)}
        hint={hint}
      />

      <Container top={6}>
        <Row>
          <Body7 isLowContrast>{`${String(value).length.toLocaleString()} characters`}</Body7>
        </Row>
      </Container>

      {showVariables && (
        <Container top={16}>
          <MicroLabel>
            {variables ? `Variables a ${documentsKind} run provides` : 'Variables a run provides'}
          </MicroLabel>
          {variables && (
            <Container top={6}>
              <Row>
                {variables.map((variable) => (
                  <Chip key={variable}>{`{{${variable}}}`}</Chip>
                ))}
              </Row>
            </Container>
          )}
          <Container top={6}>
            <Consequence>
              {variables
                ? 'The engine builds this namespace from the documents kind. A name outside it does not render empty — it raises, and every unit of the run fails.'
                : 'Choose a documents kind and this rail lists the variables that kind’s runs provide.'}
            </Consequence>
          </Container>
          {previewLies && (
            <Container top={6}>
              <Consequence $tone="warn">
                A preview run does not use this set. preview_score substitutes the document-shaped context for every
                spec, so it can pass a prompt a real run kills, and reject one a real run would have been fine with.
              </Consequence>
            </Container>
          )}
        </Container>
      )}

      {unknown.length > 0 && (
        <Container top={8}>
          <Consequence $tone="warn">
            {`${unknown.map((v) => `{{${v}}}`).join(', ')} ${
              unknown.length === 1 ? 'is' : 'are'
            } not in a ${documentsKind} run’s namespace. The engine renders with StrictUndefined, so this does not come out empty — the render raises, every unit fails with template_render_failed, and a permanent error does not retry.`}
          </Consequence>
        </Container>
      )}
    </div>
  );
};
